import { byJob, isAssigneeOf, isLeader } from './derive';
import {
  KST,
  addWorkingMinutes,
  formatClock,
  formatDue,
  isMemberWorking,
  nextWorkStart,
  workingMinutesBetween,
} from './time';

/** Complete project(동료 평가) 상태·문항·챕터 계산 */

export const WINDOW_MINUTES = 180;

export const BEHAVIORS = [
  { key: 'handoff_ready', label: 'Handed off work that was ready to use' },
  { key: 'flag_early', label: 'Flagged delays or blockers early' },
  { key: 'answer_clear', label: 'Answered questions clearly and in time' },
  { key: 'apply_feedback', label: 'Took feedback and applied it' },
  { key: 'help_teammates', label: 'Helped teammates move forward when stuck' },
];

export const SCALE = ['Not at all', 'Rarely', 'Sometimes', 'Often', 'Consistently'];

export const STRENGTHS = [
  { key: 'clear_communication', label: 'Clear communication' },
  { key: 'reliable_deadlines', label: 'Reliable on deadlines' },
  { key: 'strong_handoffs', label: 'Strong handoffs' },
  { key: 'problem_solving', label: 'Problem solving' },
  { key: 'learns_from_feedback', label: 'Learns from feedback' },
  { key: 'supports_team', label: 'Supports the team' },
];

export const HELP = [
  [
    'What this is',
    'The last step of the project. You write one chapter for each teammate about how you actually worked together, then pick your Kit items (keeping none is fine). Retrospective is optional.',
  ],
  [
    'Why it matters',
    'You complete the project when you stay until the end and submit your own peer review. Without it, the project isn’t completed for you, even if your team finished. No report and no certificate.',
  ],
  [
    'When it opens',
    'When the leader submits the final deliverable. That one submission opens peer review for the whole team. If it never comes, it opens when the project ends. If the final deliverable is rejected and resubmitted, your 3-hour window restarts.',
  ],
  [
    'Who reviews whom',
    'Every active teammate reviews every other active teammate, including the leader. Members who dropped out and staff backup accounts don’t review and aren’t reviewed.',
  ],
  [
    'Deadline',
    'On time: within 3 working hours after the final submission. Offline hours don’t count. Late: until 24 hours after the project ends. It still counts, but your report shows “Peer review submitted late” and Kit reference sentences can’t be used. After that it closes.',
  ],
  [
    'Where it goes',
    'Your teammates’ chapters about you go into your growth report. Yours go into theirs. It’s the main input to that report, so it shapes what each person takes away.',
  ],
  [
    'Please be honest',
    'Write what you actually saw, good and bad. Be specific and fair. Polite praise doesn’t help anyone grow. Honest reviews are what make the report useful for your teammates, and theirs for you.',
  ],
].map(([title, body]) => ({ title, body, em: title === 'Please be honest' }));

export const kstDue = (at) => `${formatDue(at, KST)} KST`;
export const kstClock = (at) => `${formatClock(new Date(at), KST)} KST`;

export const isReviewer = (m) =>
  !!m && m.role !== 'npc' && !m.staff && !m.staffBackup && (m.status ?? 'active') === 'active';

const mineOf = (data) => {
  const cp = data?.completeProject;
  if (!cp) return null;
  return { ...cp, ...(cp.byMember?.[data.viewerId] ?? {}) };
};

const NONE = { eligible: false, kind: 'none' };

export const cpState = (data, viewer, now) => {
  const cp = mineOf(data);
  if (!cp || cp.eligible === false || data.readOnly || !isReviewer(viewer)) return NONE;
  const t = now.getTime();
  const ms = (iso) => (iso ? new Date(iso).getTime() : null);
  const opensAt = ms(cp.opensAt);
  const closesAt = ms(cp.closesAt);
  let dueAt = ms(cp.dueAt);
  if (dueAt == null && cp.windowStartAt) {
    dueAt = addWorkingMinutes(cp.windowStartAt, WINDOW_MINUTES, viewer).getTime();
  }
  if (dueAt != null && closesAt != null) dueAt = Math.min(dueAt, closesAt);

  let kind;
  if (cp.submittedAt) kind = 'done';
  else if (opensAt == null || t < opensAt) kind = 'locked';
  else if (closesAt != null && t >= closesAt) kind = 'closed';
  else kind = 'open';

  let step = -1;
  if (kind === 'done') step = cp.late ? 1 : 0;
  else if (kind === 'open') step = dueAt != null && t >= dueAt ? 1 : 0;
  else if (kind === 'closed') step = 2;

  const opener = (data.members ?? []).find((m) => m.id === cp.openedById);
  let byWho = 'the project ended';
  if (cp.trigger === 'final') {
    if (cp.openedById === viewer.id) byWho = 'you submitted the final deliverable';
    else {
      byWho = `${opener?.name ?? 'Your leader'}${isLeader(opener) ? ' (leader)' : ''} submitted the final deliverable`;
    }
  }

  const offline = !isMemberWorking(now, viewer);
  const paused = kind === 'open' && step === 0 && offline;
  return {
    eligible: true,
    kind,
    step,
    trigger: cp.trigger,
    opensAt: cp.opensAt,
    windowStartAt: cp.windowStartAt ?? cp.opensAt,
    reset: !!cp.reset,
    byWho,
    dueAt: dueAt != null ? new Date(dueAt) : null,
    closesAt: closesAt != null ? new Date(closesAt) : null,
    submittedAt: cp.submittedAt ?? null,
    late: !!cp.late,
    leftMin: step === 0 && dueAt != null ? workingMinutesBetween(now, new Date(dueAt), viewer) : 0,
    paused,
    resumesAt: paused ? nextWorkStart(now, viewer) : null,
    lateLeftMin: closesAt != null ? Math.max(0, Math.ceil((closesAt - t) / 60000)) : 0,
  };
};

export const cpNeedsAttention = (cp) => cp.eligible && (cp.kind === 'open' || cp.kind === 'closed');

export const matesOf = (data, viewer) =>
  (data.members ?? []).filter((m) => m.id !== viewer?.id && isReviewer(m)).sort(byJob);

export const EMPTY_DRAFT = {
  chapters: {},
  retro: { learned: '', nextGoal: '' },
  kit: { picks: [], decided: false },
};

export const normalizeDraft = (d) => ({
  chapters: d?.chapters ?? {},
  retro: { learned: d?.retro?.learned ?? '', nextGoal: d?.retro?.nextGoal ?? '' },
  kit: { picks: d?.kit?.picks ?? [], decided: !!d?.kit?.decided },
});

const blank = (s) => !String(s ?? '').trim();

export const missingOf = (a) => ({
  scores: BEHAVIORS.map((b) => a?.scores?.[b.key] == null),
  strength: a?.strength == null,
  praise: blank(a?.praise),
  improve: blank(a?.improve),
  workAgain: a?.workAgain == null,
});

export const countMissing = (mm) =>
  mm.scores.filter(Boolean).length +
  ['strength', 'praise', 'improve', 'workAgain'].filter((k) => mm[k]).length;

const isEmptyAnswer = (a) =>
  !a ||
  (BEHAVIORS.every((b) => a.scores?.[b.key] == null) &&
    a.strength == null &&
    blank(a.praise) &&
    blank(a.improve) &&
    blank(a.thanks) &&
    a.workAgain == null);

export const chapterStatus = (a) => {
  if (isEmptyAnswer(a)) return 'none';
  return countMissing(missingOf(a)) ? 'progress' : 'done';
};

export const retroStatus = (r) => {
  if (blank(r?.learned) && blank(r?.nextGoal)) return 'none';
  return !blank(r?.learned) && !blank(r?.nextGoal) ? 'done' : 'progress';
};

export const kitStatus = (k) => {
  if (k?.decided) return 'done';
  return k?.picks?.length ? 'progress' : 'none';
};

export const STATUS_CHIP = {
  none: { label: 'Not started', tone: 'gray' },
  progress: { label: 'In progress', tone: 'green' },
  done: { label: 'Done', tone: 'success' },
};

const firstName = (m) => (m?.name ?? 'Someone').split(' ')[0];

const duration = (a, b) => {
  const mins = Math.max(1, Math.round((new Date(b) - new Date(a)) / 60000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  return mins % 60 ? `${h}h ${mins % 60}m` : `${h}h`;
};

const outcome = (t) => {
  const v = t.version ?? 1;
  switch (t.state) {
    case 'approved':
      if (v <= 1) return 'approved first time';
      return `${v === 2 ? 'rejected once' : `rejected ${v - 1} times`}, v${v} approved`;
    case 'submitted':
      return 'in review';
    case 'rework_requested':
      return 'changes requested';
    case 'in_progress':
      return 'in progress';
    case 'declined':
      return 'declined';
    default:
      return 'not started';
  }
};

export const workedTogether = (data, viewer, mate) => {
  const me = viewer.id;
  const out = [];
  data.tasks
    .filter((t) => t.state !== 'cancelled')
    .forEach((t) => {
      if (isAssigneeOf(t, mate.id) && t.receiverId === me) {
        out.push({ t: t.title, d: `${firstName(mate)} → you · ${outcome(t)}` });
      } else if (isAssigneeOf(t, me) && t.receiverId === mate.id) {
        out.push({ t: t.title, d: `You → ${firstName(mate)} · ${outcome(t)}` });
      }
    });
  const taskTitle = (id) => data.tasks.find((t) => t.id === id)?.title ?? 'a task';
  (data.comments ?? [])
    .filter(
      (c) =>
        !c.parentId &&
        (c.type === 'question' || c.type === 'blocker') &&
        ((c.authorId === mate.id && c.targetUserId === me) ||
          (c.authorId === me && c.targetUserId === mate.id)),
    )
    .forEach((c) => {
      const askerIsMe = c.authorId === me;
      const reply = (data.comments ?? []).find(
        (r) => r.parentId === c.id && r.authorId === c.targetUserId,
      );
      const asker = askerIsMe ? 'You' : firstName(mate);
      const verb = c.type === 'blocker' ? 'flagged a blocker' : 'asked';
      const answerer = askerIsMe ? firstName(mate) : 'you';
      out.push({
        t: `${c.type === 'blocker' ? 'Blocker' : 'Question'} on “${taskTitle(c.taskId)}”`,
        d: reply
          ? `${asker} ${verb} · ${answerer} answered in ${duration(c.createdAt, reply.createdAt)}`
          : `${asker} ${verb} · no answer on the board`,
      });
    });
  return out.slice(0, 6);
};

export const kitCandidates = (data, viewer) => {
  const cpName = new Map((data.checkpoints ?? []).map((c) => [c.id, c.name]));
  const checkins = (data.checkins ?? [])
    .filter((c) => c.userId === viewer.id && !blank(c.note))
    .sort((a, b) => new Date(a.checkedInAt) - new Date(b.checkedInAt))
    .map((c) => ({
      type: 'checkin',
      id: c.checkpointId,
      tag: `Check-in · ${cpName.get(c.checkpointId) ?? 'Checkpoint'}`,
      text: c.note,
    }));
  const handoffs = data.tasks
    .filter((t) => isAssigneeOf(t, viewer.id) && !blank(t.deliverable?.summary))
    .map((t) => ({
      type: 'handoff',
      id: t.id,
      tag: `Handoff · ${t.title}`,
      text: t.deliverable.summary,
    }));
  return [...checkins, ...handoffs];
};

export const samePick = (a, b) => a.type === b.type && String(a.id) === String(b.id);
